/**
 * Page meta utilities.
 * Retrieves page tracking information from meta tags.
 *
 * @module utils/page-meta
 */

/** Keys for page info meta tags */
type PageInfoKey = 'pageSection' | 'pageName' | 'pageStage' | 'pageDept'

/** Page info object with optional values for each meta tag key */
type PageInfo = Partial<Record<PageInfoKey, string>>

/**
 * Retrieves page tracking information from document meta tags.
 * Reads meta tags named 'pageSection', 'pageName', and 'pageStage'.
 *
 * @returns An object containing the page info values found in meta tags
 *
 * @example
 * // With meta tags: <meta name="pageSection" content="Home">
 * const info = getPageInfo()
 * // Returns: { pageSection: 'Home' }
 */
export function getPageInfo(): PageInfo {
  if (typeof document === 'undefined') return {}

  const infoKeys: PageInfoKey[] = ['pageSection', 'pageName', 'pageStage', 'pageDept']
  const pageInfo = infoKeys.reduce<PageInfo>((acc, key) => {
    const content = document.querySelector(`meta[name="${key}"]`)?.getAttribute('content')
    if (content) {
      acc[key] = content
    }
    return acc
  }, {})
  return pageInfo
}
